// AdditionalTaskQueueDialog.js
import React, { useState, useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Button, Typography } from '@mui/material';
import { UtilityLibrary } from '../../ComponentsLibrary';

const DEFAULT_COLUMNS = [
  { field: 'workflow', headerName: 'Workflow', flex: 1 },
  { field: 'queue', headerName: 'Queue', flex: 1 },
  { field: 'nextActionType', headerName: 'Next Action Type', flex: 1 },
  { field: 'priority', headerName: 'Priority', flex: 1 },
  { field: 'dateEntered', headerName: 'Date Entered Queue', flex: 1 },
];

const sx_grid = {
    "& .MuiDataGrid-row:hover": {
        cursor: "pointer"
    },
    ".MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel": {
        "margin-top": "1em",
        "margin-bottom": "1em"
    },
    "& .MuiDataGrid-virtualScroller": { overflowX: "auto", overflowY: "auto" }
}

const AdditionalTaskQueueDialog = ({
  caseNumber,
  rows,
  onWork,    // (selectedRow) => void
  onClose,   // () => void
}) => {
  const [paginationModel, setPaginationModel] = useState({ pageSize: 5, page: 0 });
  const [selectedRowId, setSelectedRowId] = useState(null);

  const selectedRow = useMemo(
    () => (rows ?? []).find((r) => r.caseWorkflowID === selectedRowId) ?? null,
    [rows, selectedRowId]
  );

  const extractNumericByKeyHints = (obj, hints) => {
    if (!obj || typeof obj !== 'object') return null;
    let found = null;
    const keys = Object.keys(obj || {});
    for (const key of keys) {
      const lower = key.toLowerCase();
      for (const hint of hints) {
        if (lower.includes(hint)) {
          const val = obj[key];
          const num = Number(val);
          if (Number.isFinite(num)) {
            found = num;
            return found;
          }
        }
      }
      // nested
      if (obj[key] && typeof obj[key] === 'object') {
        const nested = extractNumericByKeyHints(obj[key], hints);
        if (Number.isFinite(nested)) return nested;
      }
    }
    return found;
  };

  const computeOverallVariance = (dataRows) => {
    if (!Array.isArray(dataRows) || dataRows.length === 0) return null;
    let totalWork = 0;
    let totalStd = 0;

    for (const r of dataRows) {
      const work = extractNumericByKeyHints(r, ['work', 'actionwork', 'action_work', 'worktime']) ?? extractNumericByKeyHints(r, ['workseconds', 'work_seconds']);
      const std = extractNumericByKeyHints(r, ['std', 'standard', 'standardtotal', 'standard_total', 'stdtime']) ?? extractNumericByKeyHints(r, ['stdseconds', 'std_seconds']);

      if (Number.isFinite(Number(work))) totalWork += Number(work);
      if (Number.isFinite(Number(std))) totalStd += Number(std);
    }

    if (!Number.isFinite(totalWork) || !Number.isFinite(totalStd) || totalStd === 0) return null;
    const percent = ((totalWork - totalStd) / totalStd) * 100;
    return `${Math.round(percent)}%`;
  };

  const overallVariance = useMemo(() => computeOverallVariance(rows), [rows]);

  const handleWork = () => {
    if (selectedRow) onWork(selectedRow);
  };

  const mapDataToGrid = (dataToMap) => {
    return dataToMap.map((item, index) => ({
      id: item.caseWorkflowID, 
      workflow: item.workflow.workflowName ?? '',
      queue: item.workflowStep.wS_DepartmentName ?? '',
      nextActionType: item.workflowStep.workflowStepName ?? '',
      priority: item.workflowStep.stepPriority ?? '',
      dateEntered: UtilityLibrary.formatDate(item.nextStepDueTS, "MM/DD/YYYY") ?? '',
    }));
  };

  const handleRowDoubleClick = (params) => {
    const raw = (rows ?? []).find((r) => r.caseWorkflowID === params.id);
    if (raw) onWork(raw);
  };

  return (
    <div className='flex-main-frame'>
      <div className='form-main-container'>
        <div className='form-content' style={{ width: '100%' }}>
          <div className='form-dynamic-content'>
            <form className="form-dynamic-grid">
              <div className='form-row'>
                <div className='form-group'>
                  There are one or more additional tasks in the Queue on case #{caseNumber}.
                  If you would like to work another task, select a row from the table and click WORK.
                  To return to the Work tab without selecting a task, click CLOSE.
                </div>
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  {overallVariance ? (
                    <div style={{ marginBottom: 8 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Overall Variance: {overallVariance}
                      </Typography>
                    </div>
                  ) : null}

                  <DataGrid
                    id="addTaskQueueDgrd"
                    rows={mapDataToGrid(rows)}
                    columns={DEFAULT_COLUMNS}
                    rowHeight={30}
                    pagination
                    pageSizeOptions={[5, 10, 15, 20, 25]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    onRowSelectionModelChange={(ids) => setSelectedRowId(ids[0] ?? null)}
                    rowSelectionModel={selectedRowId ? [selectedRowId] : []}
                    onRowDoubleClick={handleRowDoubleClick}
                    sx={sx_grid}
                  />
                </div>
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <div className="d-flex justify-content-between align-items-center mb-2 form-actions">
                    <div className="rightDiv">Total Count: {rows.length}</div>
                  </div>
                </div>
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <div className="d-flex justify-content-between align-items-center mb-2 form-actions">
                    <div className="leftDiv">
                      <Button id="closeBtn" color="ywButtonWhite" variant='outlined'
                              className='me-3' onClick={onClose}>CLOSE</Button>
                    </div>
                    <div className="rightDiv">
                      <Button id="workBtn" variant='contained' color='ywButtonBlue'
                              className='me-3' disabled={!selectedRow}
                              onClick={handleWork}>WORK</Button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AdditionalTaskQueueDialog);